import { ISelectedStatCodeData, SelectedStatCodeData } from 'Foundation/StatCode/StatModels';

export interface IUserDataServer {
  readonly dpecid: string;
  readonly name: string;
  readonly email: string;
  readonly dpecEmail: string;
  readonly userType: string;
  readonly state: string;
  readonly userId: string;
  readonly classification: string;
  selectedStatCodeData: ISelectedStatCodeData | undefined;
  phoneList: Phone[] | null | undefined;
  isMockingAgent: boolean;
  isAISGUser: boolean;
  eCliqCodes: string | null | undefined;
  readonly channel: 'BL' | 'PL' | '';
}

export interface Phone {
  PhoneTypeCode: string;
  PhoneNumber: string;
  PhoneExtension: string;
  PartyPhoneSoftDeleteIndicator: string;
}

export interface IUserData {
  readonly dpecid: string;
  readonly name: string;
  readonly email: string;
  readonly dpecEmail: string;
  readonly userType: string;
  readonly state: string;
  readonly userId: string;
  readonly classification: string;
  selectedStatCodeData: ISelectedStatCodeData;
  phoneList: Phone[] | null | undefined;
  eCliqCodes: string | null | undefined;
  isMockingAgent: boolean;
  isAISGUser: boolean;
  readonly channel: 'BL' | 'PL' | '';
  readonly isBL: () => boolean;
  readonly isPL: () => boolean;
  readonly isAgent: () => boolean;
  firstName: () => string;
  lastName: () => string;
  /**
   * Returns userId without any suffix;
   * The last suffix is appended by auth provider;
   * userIds are permitted to have '@' in them so this may cut off some parts of the userID for some users.
   */
  userIdNoSuffix: () => string;

  /**
   * Returns userId without the last suffix, which is appended by auth provider;
   * userIds are permitted to have '@' in them so we may want to preserve suffixes prior to the last one.
   */
  userIdNoAuthSuffix: () => string | void;
}

export class UserDataObject implements IUserData {
  dpecid: string;
  name: string;
  email: string;
  dpecEmail: string;
  userType: string;
  state: string;
  userId: string;
  classification: string;
  selectedStatCodeData: ISelectedStatCodeData;
  phoneList: Phone[] | null | undefined;
  eCliqCodes: string | null | undefined;
  isMockingAgent: boolean;
  isAISGUser: boolean;
  channel: 'BL' | 'PL' | '';
  constructor(userData: IUserDataServer) {
    this.dpecid = userData?.dpecid ?? '';
    this.name = userData?.name ?? '';
    this.email = userData?.email ?? '';
    this.dpecEmail = userData?.dpecEmail ?? '';
    this.userType = userData?.userType ?? '';
    this.state = userData?.state ?? '';
    this.userId = userData?.userId ?? '';
    this.classification = userData?.classification ?? '';
    this.selectedStatCodeData = userData?.selectedStatCodeData ?? (new SelectedStatCodeData() as ISelectedStatCodeData);
    this.phoneList = userData?.phoneList ?? [];
    this.eCliqCodes = userData?.eCliqCodes ?? '';
    this.isMockingAgent = userData?.isMockingAgent ?? false;
    this.isAISGUser = userData?.isAISGUser ?? false;
    this.channel = userData?.channel ?? '';
    // The below are required to allow for copying using spread in usercontext. Maybe another better solution?
    // eslint-disable-next-line no-self-assign
    this.isBL = this.isBL;
    // eslint-disable-next-line no-self-assign
    this.isPL = this.isPL;
    // eslint-disable-next-line no-self-assign
    this.isAgent = this.isAgent;
    // eslint-disable-next-line no-self-assign
    this.firstName = this.firstName;
    // eslint-disable-next-line no-self-assign
    this.lastName = this.lastName;
    //these are the values causing errors for nonprod users when refreshing for PL
    this.userIdNoSuffix = this.userIdNoSuffix;
    this.userIdNoAuthSuffix = this.userIdNoAuthSuffix;
  }
  isBL(): boolean {
    return this.channel === 'BL';
  }
  isPL(): boolean {
    return this.channel === 'PL';
  }
  isAgent(): boolean {
    return !(this.userType?.toLowerCase() === 'employee');
  }
  firstName(): string {
    let firstName = this?.name?.split(' ')[0];
    return this.capitalizeFullName(firstName);
  }
  lastName(): string {
    const start = this?.name?.indexOf(' ');
    let lastName = this.name.substring(start + 1);
    return this.capitalizeFullName(lastName);
  }

  capitalizeFullName(name: string): string {
    try {
      if (name === name.toLowerCase()) {
        name = this.capitalizeFirstLetter(name, ' ');
        name = this.capitalizeFirstLetter(name, '-');
      }
    } catch {}
    return name;
  }

  capitalizeFirstLetter(name: string, sep: string): string {
    let names = name.split(sep);
    for (var i = 0; i < names.length; i++) {
      names[i] = names[i][0].toUpperCase() + names[i].substring(1);
    }
    return names.join(sep);
  }

  userIdNoSuffix(): string {
    try {
      return this.userId.split('@')[0];
    } catch {}
    return '';
  }

  userIdNoAuthSuffix(): string | void {
    try {
      if (this.userId) {
        const lastIndex = this.userId.lastIndexOf('@');
        if (lastIndex === -1) return this.userId;
        else if (lastIndex > 0) return this.userId.substring(0, lastIndex);
        else return this.userId;
      }
    } catch {}
  }
}

export interface IAgencyRewardData {
  ID: string;
  IsAvailable: boolean;
  CouponCode: string;
  AmountAvailable: string;
  AmountUsed: string;
  Eligibility: string;
}
export interface IAgencyCouponData {
  ID: string;
  Code: string;
}

export interface IMarketingInitiative {
  Initative: IMIInitiative;
  Agent: IMIAgent;
}
export interface IMIInitiative {
  Name: string;
  InitiativeType: string;
  Source: string;
  Status: string;
  Channel: string;
  CreatedDate: string;
  Description: string;
  ParentId: string;
  ParentName: string;
  StartDate: string;
  EndDate: string;
}
export interface IMIAgent {
  InitiativeName: string;
  UserID: string;
  TriggeringOM: string;
  TriggeringSubOM: string;
  LdapUserID: string;
  TriggeringClMaster: string;
  TriggeringClSub: string;
  CreationDate: string;
  SelectedState: string;
  Status: string;
}
